import { useMemo } from "react"

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { DataTable } from "@/components/ui/data-table"
import { SalesStatusEnum } from "@/enums"
import { DateHelper, getStatusInfo } from "@/helpers"
import { useTransactionById } from "@/hooks"
import { cn } from "@/lib/utils"

import { AppBadge } from "../commons"
import { transactionColumns } from "./columns"
interface TransactionDetailsProps {
  transactionId: string
}
export const TransactionDetails = ({
  transactionId,
}: TransactionDetailsProps) => {
  const { data: transaction, isFetching } =
    useTransactionById(transactionId)
  const orderValue = transaction?.orderValue ?? 0
  const netValue = transaction?.netValue ?? 0
  const methods = useMemo(() => {
    if (!transaction) return []
    return transaction.methods.map((method) => ({
      ...method,
      installments: method.installments.map(
        (installment) => ({
          ...installment,
          numberOfInstallments: method.numberOfInstallments,
          transactionStatus: transaction.status,
        })
      ),
    }))
  }, [transaction])
  if (isFetching) {
    return <div>Carregando...</div>
  }
  const statusMessage: Record<SalesStatusEnum, string> = {
    [SalesStatusEnum.AUTHORIZED]:
      "O pagamento deste pedido foi efetivado com sucesso.",
    [SalesStatusEnum.FUTURE]:
      "O pagamento ainda sera realizado.",
    [SalesStatusEnum.CANCELLED]:
      "O pagamento desta transação foi cancelado.",
    [SalesStatusEnum.PAYMENT_ERROR]:
      "O pagamento deste pedido não foi efetivado devido a erro.",
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-2xl">
            Detalhes da transação
          </CardTitle>
          <AppBadge
            getBadgeInfo={() =>
              getStatusInfo(
                transaction?.status as SalesStatusEnum
              )
            }
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        <section
          data-status={transaction?.status}
          className="group"
        >
          <div className="rounded-md border-2 p-4 data-[status=FUTURE]:bg-yellow-500/20 group-data-[status=AUTHORIZED]:border-green-500/30 group-data-[status=CANCELLED]:border-destructive/30 group-data-[status=FUTURE]:border-yellow-500/30 group-data-[status=PAYMENT\_ERROR]:border-orange-500/30 group-data-[status=AUTHORIZED]:bg-green-500/20 group-data-[status=CANCELLED]:bg-destructive/20 group-data-[status=PAYMENT\_ERROR]:bg-orange-500/20">
            <p className="text-sm">
              {
                statusMessage[
                  transaction?.status as SalesStatusEnum
                ]
              }
            </p>
          </div>
        </section>
        <section className="grid gap-4 xl:grid-cols-3">
          <div className="space-y-2 rounded-md border border-border bg-sidebar p-4">
            <h3 className="font-semibold">
              Informações do pedido
            </h3>
            <div className="text-sm text-muted-foreground">
              <p>
                <span className="font-medium text-zinc-950">
                  Nome do usuário:
                </span>{" "}
                {transaction?.customer.name}
              </p>
              <p>
                <span className="font-medium text-zinc-950">
                  Número do pedido:
                </span>{" "}
                {transaction?.orderCode}
              </p>
              <p>
                <span className="font-medium text-zinc-950">
                  Data da compra:
                </span>{" "}
                {transaction?.orderDate}
              </p>
            </div>
          </div>
          <div className="space-y-2 rounded-md border border-border bg-sidebar p-4">
            <h3 className="font-semibold">
              Valor total bruto
            </h3>
            <strong className="block text-2xl font-bold text-primary">
              {DateHelper.formatMoney(orderValue / 100)}
            </strong>
            <p className="text-sm text-muted-foreground">
              *valor total das compra sem desconto das taxas
              e encargos
            </p>
          </div>
          <div className="space-y-2 rounded-md border border-border bg-sidebar p-4">
            <h3 className="font-semibold">
              Valor total líquido (a receber)
            </h3>
            <strong className="block text-2xl font-bold text-primary">
              {DateHelper.formatMoney(netValue / 100)}
            </strong>
            <p className="text-sm text-muted-foreground">
              *valor total da compra com desconto das taxas
              e encargos
            </p>
          </div>
        </section>
        <section className="space-y-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-muted-foreground">
              Detalhes da transação
            </h3>
            <div
              className={cn(
                "flex items-center gap-2",
                transaction?.methods[0].thumbUrl && "-mt-3"
              )}
            >
              <strong>
                {transaction?.methods[0].description}
              </strong>
              {transaction?.methods[0].thumbUrl && (
                <img
                  src={transaction?.methods[0].thumbUrl}
                  alt={
                    transaction?.methods[0]?.emitter || ""
                  }
                  className="size-[40px] sm:size-[50px]"
                />
              )}
            </div>
          </div>
          <DataTable
            columns={transactionColumns}
            data={methods[0].installments}
            withFooter
          />
        </section>
      </CardContent>
    </Card>
  )
}
